import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  ArrowLeft, Save, Bot, Brain, Settings
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AIActorModelsSettings() {
  const { actorId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    openrouter_model: "anthropic/claude-3.5-sonnet",
    email_analysis_model: "anthropic/claude-3.5-sonnet",
    email_reply_model: "anthropic/claude-3.5-sonnet",
    email_analysis_enabled: true,
    confidence_threshold: 0.80,
    auto_learn: true,
    analysis_char_limit: 4000,
    analysis_temperature: 0.2,
    analysis_custom_categories: ["Promotional", "Newsletter", "Personal", "Work"],
    analysis_system_prompt: null,
    analysis_debug_mode: false
  });
  
  const [models, setModels] = useState([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [actor, setActor] = useState(null);
  const [analyzingEmails, setAnalyzingEmails] = useState(false);
  const [analysisStatus, setAnalysisStatus] = useState({
    status: "not_started",
    progress: 0,
    total_emails: 0,
    category_distribution: {}
  });
  const [newCategory, setNewCategory] = useState("");
  
  const pollIntervalRef = useRef(null);

  useEffect(() => {
    fetchActorDetails();
    fetchSettings();
    fetchModels();
    fetchAnalysisStatus();
    
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [actorId]);

  const fetchActorDetails = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/ai/actors/${actorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActor(response.data);
    } catch (error) {
      console.error("Failed to fetch actor details:", error);
    }
  };

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API}/admin/ai-email/settings/${actorId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSettings(response.data);
    } catch (error) {
      console.error("Failed to fetch settings", error);
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const fetchModels = async () => {
    try {
      setLoadingModels(true);
      const response = await axios.get(`${API}/openrouter/models`);
      setModels(response.data.models || []);
    } catch (error) {
      console.error("Failed to fetch models", error);
      toast.error("Failed to load models from OpenRouter");
    } finally {
      setLoadingModels(false);
    }
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem("token");
      await axios.put(
        `${API}/admin/ai-email/settings/${actorId}`,
        settings,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Settings saved successfully");
    } catch (error) {
      console.error("Failed to save settings", error);
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleAddCategory = () => {
    if (!newCategory.trim()) {
      toast.error("Category name cannot be empty");
      return;
    }
    
    const categories = settings.analysis_custom_categories || [];
    if (categories.includes(newCategory.trim())) {
      toast.error("Category already exists");
      return;
    }
    
    setSettings({
      ...settings,
      analysis_custom_categories: [...categories, newCategory.trim()]
    });
    setNewCategory("");
    toast.success("Category added");
  };

  const handleRemoveCategory = (category) => {
    const categories = settings.analysis_custom_categories || [];
    setSettings({
      ...settings,
      analysis_custom_categories: categories.filter(c => c !== category)
    });
    toast.success("Category removed");
  };

  const handleResetToDefaults = () => {
    setSettings({
      ...settings,
      analysis_char_limit: 4000,
      analysis_temperature: 0.2,
      analysis_custom_categories: ["Promotional", "Newsletter", "Personal", "Work"],
      analysis_system_prompt: null,
      analysis_debug_mode: false
    });
    toast.success("Reset to default settings");
  };

  const fetchAnalysisStatus = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API}/admin/actors/${actorId}/analysis-status`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const status = response.data;
      setAnalysisStatus(status);
      
      if (status.status === "in_progress" && !analyzingEmails) {
        setAnalyzingEmails(true);
        startPolling();
      }
      
      return status;
    } catch (error) {
      console.error("Failed to fetch analysis status", error);
      return null;
    }
  };
  
  const startPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }
    
    pollIntervalRef.current = setInterval(async () => {
      const status = await fetchAnalysisStatus();
      if (status && status.status === "completed") {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
        setAnalyzingEmails(false);
        toast.success(`Analysis complete! ${status.total_emails} emails categorized.`);
      } else if (status && status.status === "failed") {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
        setAnalyzingEmails(false);
        toast.error("Analysis failed: " + (status.error || "Unknown error"));
      }
    }, 3000);
    
    setTimeout(() => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
        if (analyzingEmails) {
          setAnalyzingEmails(false);
          toast.warning("Analysis is taking longer than expected. Check back later.");
        }
      }
    }, 1800000);
  };

  const handleReanalyzeEmails = async (forceRecategorize = false) => {
    try {
      setAnalyzingEmails(true);
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/admin/actors/${actorId}/analyze-emails?force_recategorize=${forceRecategorize}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message || "Email analysis started");
      startPolling();
    } catch (error) {
      console.error("Failed to start email analysis", error);
      toast.error(error.response?.data?.detail || "Failed to start email analysis");
      setAnalyzingEmails(false);
    }
  };

  const handleBack = () => {
    navigate(`/ai/actors/manage/${actorId}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <div className="container max-w-6xl mx-auto p-6 min-h-screen">
      {/* Header Section */}
      <div className="mb-8">
        <Button
          variant="ghost"
          onClick={handleBack}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Manage
        </Button>
        
        <div className="text-center">
          <h1 className="text-3xl font-bold mb-2">Models & Settings</h1>
          {actor && (
            <p className="text-muted-foreground text-lg">
              {actor.name}
              {actor.experience_level && ` • ${actor.experience_level}`}
            </p>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="space-y-6">
        {/* AI Model Selection Card */}
        <Card>
          <CardHeader>
            <CardTitle>AI Model Selection</CardTitle>
            <CardDescription>
              Choose the AI model for email analysis and automation from OpenRouter
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {loadingModels ? (
              <p className="text-sm text-muted-foreground">Loading models...</p>
            ) : (
              <>
                <div>
                  <label className="text-sm font-medium mb-2 block">AI Chat Model</label>
                  <select
                    className="w-full border rounded px-3 py-2"
                    value={settings.openrouter_model}
                    onChange={(e) => setSettings({ ...settings, openrouter_model: e.target.value })}
                  >
                    {models.map((model) => (
                      <option key={model.id} value={model.id}>
                        {model.name} - Context: {model.context_length.toLocaleString()} tokens
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Model used for AI chat and email assistance
                  </p>
                </div>
                
                <div>
                  <label className="text-sm font-medium mb-2 block">Email Analysis Model</label>
                  <select
                    className="w-full border rounded px-3 py-2"
                    value={settings.email_analysis_model || settings.openrouter_model}
                    onChange={(e) => setSettings({ ...settings, email_analysis_model: e.target.value })}
                  >
                    {models.map((model) => (
                      <option key={model.id} value={model.id}>
                        {model.name} - Context: {model.context_length.toLocaleString()} tokens
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Model used for categorizing emails (Promotional, Newsletter, Personal, Work)
                  </p>
                </div>
                
                <div>
                  <label className="text-sm font-medium mb-2 block">AI Email Reply Model</label>
                  <select
                    className="w-full border rounded px-3 py-2"
                    value={settings.email_reply_model || settings.openrouter_model}
                    onChange={(e) => setSettings({ ...settings, email_reply_model: e.target.value })}
                  >
                    {models.map((model) => (
                      <option key={model.id} value={model.id}>
                        {model.name} - Context: {model.context_length.toLocaleString()} tokens
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Model used for drafting new emails and replies based on your writing style
                  </p>
                </div>
                
                <div className="flex items-center gap-2 pt-2">
                  <input
                    type="checkbox"
                    id="email_analysis_enabled"
                    checked={settings.email_analysis_enabled !== false}
                    onChange={(e) => setSettings({ ...settings, email_analysis_enabled: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <label htmlFor="email_analysis_enabled" className="text-sm">
                    Enable LLM-based email categorization
                  </label>
                </div>
                
                <p className="text-xs text-muted-foreground mt-2">
                  {(models || []).length} models available from OpenRouter
                </p>
              </>
            )}
          </CardContent>
        </Card>

        {/* Advanced Email Analysis Configuration */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Advanced Email Analysis Configuration
            </CardTitle>
            <CardDescription>
              Fine-tune how the LLM categorizes your emails with full control and transparency
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Character Limit */}
            <div>
              <label className="text-sm font-medium mb-2 block">
                Email Body Character Limit: {settings.analysis_char_limit || 4000} chars
              </label>
              <input
                type="range"
                min="500"
                max="10000"
                step="500"
                value={settings.analysis_char_limit || 4000}
                onChange={(e) => setSettings({ ...settings, analysis_char_limit: parseInt(e.target.value) })}
                className="w-full"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Limit the number of characters sent to the LLM to reduce API costs
              </p>
            </div>

            {/* Temperature Slider */}
            <div>
              <label className="text-sm font-medium mb-2 block">
                LLM Temperature: {(settings.analysis_temperature || 0.2).toFixed(1)}
              </label>
              <input
                type="range"
                min="0.0"
                max="1.0"
                step="0.1"
                value={settings.analysis_temperature || 0.2}
                onChange={(e) => setSettings({ ...settings, analysis_temperature: parseFloat(e.target.value) })}
                className="w-full"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Lower = more consistent, Higher = more creative (recommended: 0.2 for email categorization)
              </p>
            </div>

            {/* Custom Categories */}
            <div>
              <label className="text-sm font-medium mb-2 block">
                Custom Email Categories
              </label>
              <div className="flex flex-wrap gap-2 mb-3">
                {(settings.analysis_custom_categories || []).map((category, index) => (
                  <Badge
                    key={index}
                    variant="secondary"
                    className="flex items-center gap-1 px-3 py-1"
                  >
                    {category}
                    <button
                      onClick={() => handleRemoveCategory(category)}
                      className="ml-1 hover:text-red-600"
                      title="Remove category"
                    >
                      ×
                    </button>
                  </Badge>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Add new category (e.g., Urgent, Bills)"
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddCategory()}
                  className="flex-1 border rounded px-3 py-2 text-sm"
                />
                <Button
                  onClick={handleAddCategory}
                  variant="outline"
                  size="sm"
                >
                  Add
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Define your own categories for email classification
              </p>
            </div>

            {/* System Prompt Editor */}
            <div>
              <label className="text-sm font-medium mb-2 block">
                Custom System Prompt (Optional)
              </label>
              <textarea
                placeholder={`Leave empty to use default prompt. Use placeholders:\n{{categories}} - List of categories\n{{sender}} - Email sender\n{{subject}} - Email subject\n{{body}} - Email body\n{{feedback}} - User feedback history`}
                value={settings.analysis_system_prompt || ""}
                onChange={(e) => setSettings({ ...settings, analysis_system_prompt: e.target.value || null })}
                className="w-full border rounded px-3 py-2 text-sm font-mono h-40 resize-y"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Advanced: Customize the exact prompt sent to the LLM. Leave empty for default behavior.
              </p>
            </div>

            {/* Debug Mode */}
            <div className="flex items-center gap-2 pt-2 border-t">
              <input
                type="checkbox"
                id="analysis_debug_mode"
                checked={settings.analysis_debug_mode || false}
                onChange={(e) => setSettings({ ...settings, analysis_debug_mode: e.target.checked })}
                className="w-4 h-4"
              />
              <label htmlFor="analysis_debug_mode" className="text-sm font-medium">
                Enable Debug Mode (Log all LLM interactions)
              </label>
            </div>
            <p className="text-xs text-muted-foreground">
              When enabled, every email categorization will be logged with full LLM request/response details for debugging
            </p>

            {/* Re-Analyze Emails Section */}
            <div className="pt-4 border-t">
              <label className="text-sm font-medium mb-2 block">
                Apply Configuration to Existing Emails
              </label>
              <p className="text-xs text-muted-foreground mb-3">
                After changing categories, model, or prompt settings, re-analyze your emails to apply the new configuration.
              </p>
              
              {analyzingEmails && (
                <div className="mb-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-blue-900">
                      {analysisStatus.status === "in_progress" ? "Analyzing emails..." : "Starting analysis..."}
                    </span>
                    <span className="text-sm font-semibold text-blue-900">
                      {analysisStatus.progress}%
                    </span>
                  </div>
                  <div className="w-full bg-blue-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${analysisStatus.progress}%` }}
                    />
                  </div>
                  {analysisStatus.total_emails > 0 && (
                    <p className="text-xs text-blue-700 mt-2">
                      Processing {analysisStatus.total_emails} emails...
                    </p>
                  )}
                </div>
              )}
              
              <Button
                onClick={() => handleReanalyzeEmails(true)}
                disabled={analyzingEmails}
                variant="secondary"
                className="w-full"
              >
                <Brain className="w-4 h-4 mr-2" />
                {analyzingEmails ? "Analyzing..." : "Re-Analyze All Emails with New Settings"}
              </Button>
              <p className="text-xs text-muted-foreground mt-2">
                This will clear existing categories and re-categorize all emails using your current configuration.
              </p>
              
              {analysisStatus.status === "completed" && !analyzingEmails && (
                <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-sm font-medium text-green-900 mb-1">
                    ✓ Last analysis completed
                  </p>
                  <p className="text-xs text-green-700">
                    {analysisStatus.total_emails} emails categorized
                  </p>
                  {Object.keys(analysisStatus.category_distribution || {}).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {Object.entries(analysisStatus.category_distribution).map(([category, count]) => (
                        <span key={category} className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                          {category}: {count}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Reset Button */}
            <div className="pt-4 border-t flex gap-2">
              <Button
                onClick={handleResetToDefaults}
                variant="outline"
                className="flex-1"
              >
                Reset to Defaults
              </Button>
              <Button
                onClick={saveSettings}
                disabled={saving}
                className="flex-1"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? "Saving..." : "Save Configuration"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
